import type { Ref } from 'vue'
import type { OptionsProps, SelectModelValue, SelectOption } from '../SelectTypes'
import type { SelectionEvents } from './useValue'
import { computed, onScopeDispose, shallowRef, watch } from 'vue'
import { modelValues, normalizeOption, sameOptionValue } from '../SelectTypes'
import { useOptionsResolver } from './useOptionsResolver'

interface SelectionProps extends OptionsProps {
  modelValue?: SelectModelValue
  multiple: boolean
  max: number
  valueAsObject: boolean
}

interface OptionsDep {
  committed: Ref<SelectOption[]>
  draft: Ref<SelectOption[] | undefined>
  search: Ref<string>
  searchFilter: (text: string) => boolean
  clearSearch: () => void
  setInternal: (options: SelectOption[]) => void
  update: (options: SelectOption[]) => void
  show: Ref<boolean>
}

export default function useOptions(props: SelectionProps, events: SelectionEvents, dep: OptionsDep) {
  const { committed, draft, search, searchFilter, clearSearch, setInternal, update, show } = dep
  const selection = computed(() => draft.value ?? committed.value)
  const resolver = useOptionsResolver(props, { search, searchFilter })
  const { eo, findOption, searchOptionById } = resolver
  const modelError = shallowRef<unknown>()
  let revision = 0
  let disposed = false

  function invalidateModel() {
    revision++
  }

  function isSelected(option: SelectOption) {
    return selection.value.some(selected => sameOptionValue(selected.value, option.value))
  }

  function handleOptionClick(option: SelectOption) {
    if (option.disabled) {
      return
    }
    const selected = isSelected(option)
    if (!props.multiple && selected) {
      return
    }
    if (props.multiple && !selected && props.max !== -1 && selection.value.length >= props.max) {
      return
    }
    // A pending external-model lookup must not overwrite a user's click.
    revision++
    const next = props.multiple
      ? selected
        ? selection.value.filter(item => !sameOptionValue(item.value, option.value))
        : [...selection.value, option]
      : [option]
    if (draft.value !== undefined) {
      draft.value = next
    } else {
      update(next)
      if (selected) {
        events.deselect(option)
      } else {
        events.select(option)
      }
    }
    clearSearch()
    if (!props.multiple) {
      show.value = false
    }
  }

  async function syncModel(value: SelectModelValue | undefined) {
    const version = ++revision
    modelError.value = undefined
    const values = value === '' && !findOption('') ? [] : modelValues(value)
    try {
      const options = await Promise.all(
        values.map(item => {
          if (typeof item === 'object' && item !== null) {
            const option = normalizeOption(item, props)
            return findOption(option.value) ?? option
          }
          // Refresh synthetic or cached selections from the current source first.
          return findOption(item, eo.value) ?? findOption(item, committed.value) ?? searchOptionById(item)
        }),
      )
      if (!disposed && version === revision) {
        setInternal(props.multiple ? options : options.slice(0, 1))
      }
    } catch (cause) {
      if (!disposed && version === revision) {
        modelError.value = cause
      }
    }
  }

  watch(
    [
      () => props.modelValue,
      () => props.multiple,
      () => props.valueAsObject,
      () => props.valueProp,
      () => props.labelProp,
    ],
    ([value]) => syncModel(value),
    { deep: true, immediate: true },
  )

  // Observe source changes without traversing unrelated data on option items.
  watch([() => props.options, eo], () => syncModel(props.modelValue))

  onScopeDispose(() => {
    disposed = true
    revision++
  })

  return { ...resolver, modelError, selection, isSelected, handleOptionClick, invalidateModel }
}
